import { useCallback, useEffect, useRef, useState } from 'react';
import {
  readCheckoutSession,
  clearCheckoutSession,
  openCheckoutChannel,
} from '../utils/checkoutSessionUtil';
import {
  CHECKOUT_EVENTS,
  CHECKOUT_MESSAGE_SOURCES,
  CHECKOUT_SESSION_STATES,
  CHECKOUT_PAGE_PHASES,
  CHECKOUT_PREPARE_TIMEOUT_MS,
  CHECKOUT_PROBE_TIMEOUT_MS,
  CHECKOUT_CLOSE_DELAY_MS,
} from '../constants/bookingConst';

/**
 * Picks the starting phase from whatever session is stored.
 * @param {Object|null} session
 * @returns {string} a CHECKOUT_PAGE_PHASES value.
 */
const initialPhase = (session) => {
  if (session?.state === CHECKOUT_SESSION_STATES.READY && session.record) return CHECKOUT_PAGE_PHASES.ACTIVE;
  if (session?.state === CHECKOUT_SESSION_STATES.PREPARING) return CHECKOUT_PAGE_PHASES.WAITING;
  return CHECKOUT_PAGE_PHASES.PROBING;
};

/**
 * Checkout-tab side of a checkout session, shared by both checkout pages (the
 * till's renewal and the landing page's sign-up): loads the order the opening
 * tab handed over, keeps the message channel to it, and closes this tab when
 * the checkout is over. Ported from the court-booking system.
 *
 * @param {string} sessionId - from the page URL.
 * @returns {{
 *   session: Object|null,
 *   phase: string,
 *   endCheckout: (eventType: string|null, payload?: Object|null) => void,
 * }}
 */
export const useCheckoutPageSession = (sessionId) => {
  const [session, setSession] = useState(() => readCheckoutSession(sessionId));
  const [phase, setPhase] = useState(() => initialPhase(session));
  const channelRef = useRef(null);
  // Set once the checkout has ended, so a late timer tick or message can't
  // run a second outcome.
  const endedRef = useRef(false);

  /**
   * Ends the checkout: reports the outcome (when there is one to report),
   * forgets the session and closes this tab — which puts the person back on
   * the screen they came from.
   * @param {string|null} eventType - CHECKOUT_EVENTS value to send, or null when the opening tab asked for the close.
   * @param {Object|null} [payload]
   */
  const endCheckout = useCallback((eventType, payload = null) => {
    if (endedRef.current) return;
    endedRef.current = true;
    if (eventType) channelRef.current?.post(eventType, payload);
    clearCheckoutSession(sessionId);
    setPhase(CHECKOUT_PAGE_PHASES.ENDED);
    // Short delay so the message above is delivered before the tab goes.
    // window.close() only works on a tab opened by script — which this one
    // was — so the ENDED notice is just the fallback.
    setTimeout(() => {
      try { window.opener?.focus(); } catch (error) { /* focus is best-effort */ }
      window.close();
    }, CHECKOUT_CLOSE_DELAY_MS);
  }, [sessionId]);

  // Channel to the opening tab.
  useEffect(() => {
    if (!sessionId) return undefined;
    const channel = openCheckoutChannel(sessionId, CHECKOUT_MESSAGE_SOURCES.CHECKOUT, (type, payload) => {
      if (type === CHECKOUT_EVENTS.SESSION && payload?.record) {
        if (endedRef.current) return;
        setSession(payload);
        setPhase(CHECKOUT_PAGE_PHASES.ACTIVE);
      } else if (type === CHECKOUT_EVENTS.CLOSE) {
        // The opening tab took over (backed out, or its dialog was closed).
        endCheckout(null);
      }
    });
    channelRef.current = channel;
    // Covers an order handed over before this tab finished loading, and
    // browsers where localStorage is unavailable.
    channel.post(CHECKOUT_EVENTS.REQUEST_SESSION);
    return () => channel.close();
  }, [sessionId, endCheckout]);

  // Give up waiting if the order never arrives.
  useEffect(() => {
    if (phase !== CHECKOUT_PAGE_PHASES.PROBING && phase !== CHECKOUT_PAGE_PHASES.WAITING) return undefined;
    const timeout = phase === CHECKOUT_PAGE_PHASES.PROBING ? CHECKOUT_PROBE_TIMEOUT_MS : CHECKOUT_PREPARE_TIMEOUT_MS;
    const timer = setTimeout(() => setPhase(CHECKOUT_PAGE_PHASES.INACTIVE), timeout);
    return () => clearTimeout(timer);
  }, [phase]);

  return { session, phase, endCheckout };
};
